import { useEffect, useState } from "react";
import PublicLayout from "@/components/PublicLayout";
import Seo from "@/components/Seo";
import { api } from "@/lib/api";
import { usePage } from "@/context/PagesContext";
import { mediaUrl } from "@/lib/media";

const PORTRAIT = "https://images.unsplash.com/photo-1760006548203-14857f7a9266?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const TEAM = "https://images.pexels.com/photos/31205510/pexels-photo-31205510.jpeg?auto=compress&cs=tinysrgb&w=1600";

const FALLBACK_TEAM = [
  { id: "fb1", name: "Sophie", role: "Senior Specialist, Indochina & Sri Lanka" },
  { id: "fb2", name: "Jamie", role: "Specialist, Japan, Korea & the East" },
  { id: "fb3", name: "Jo", role: "Specialist, the Islands & the Arabian Peninsula" },
  { id: "fb4", name: "Nga", role: "Field Director, Vietnam & Cambodia" },
];

export default function About() {
  const { copy, seo } = usePage("about");
  const [team, setTeam] = useState(null);

  useEffect(() => {
    api.get("/team").then((r) => setTeam(r.data || []));
  }, []);

  const specialists = team && team.length > 0 ? team : FALLBACK_TEAM;

  return (
    <PublicLayout>
      <Seo title={seo.title} description={seo.description} og_title={seo.og_title} og_description={seo.og_description} />
      <section className="pt-40 pb-12 bg-ivory">
        <div className="max-w-[1500px] mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-7">
            <p className="eyebrow">{copy("hero_eyebrow", "Our Approach")}</p>
            <h1 className="mt-6 font-serif text-5xl md:text-7xl text-jade font-light leading-[1.02]">
              {copy("hero_h1_part1", "A specialist approach")}<br/>
              <span className="italic">{copy("hero_h1_part2_italic", "to private travel.")}</span>
            </h1>
          </div>
          <div className="md:col-span-5 md:pt-12">
            <p className="text-ink/75 text-[16px] md:text-[17px] font-light leading-[1.85]">
              {copy("hero_intro", "Abbot Asia is a UK-based specialist in private guided journeys across Asia and the Arabian Peninsula. We design tailor-made travel for people who want expert planning, trusted local guides, and a journey shaped around their own pace, interests, and style.")}
            </p>
          </div>
        </div>
      </section>

      <section className="py-20 bg-ivory">
        <div className="max-w-[1500px] mx-auto px-6 md:px-12">
          <img src={mediaUrl(copy("hero_image", PORTRAIT))} alt="A quiet artisan moment" className="w-full aspect-[16/8] object-cover" />
        </div>
      </section>

      <section className="bg-ivory py-24 md:py-32">
        <div className="max-w-[1500px] mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <p className="eyebrow">{copy("howwework_eyebrow", "How We Work")}</p>
          </div>
          <div className="md:col-span-8 space-y-8">
            <p className="font-serif text-3xl md:text-4xl text-jade font-light italic leading-[1.3]">
              {copy("howwework_quote", "\"Every trip begins with a real conversation.\"")}
            </p>
            <p className="text-ink/80 text-[16px] md:text-[17px] font-light leading-[1.85]">
              {copy("howwework_body_1", "Some travellers come to us for culture and history. Others want food, wildlife, family time, island stays, wellness, or a seamless multi-country journey. Our role is to listen carefully, plan intelligently, and connect you with the right places, people, hotels, and guides.")}
            </p>
            <p className="text-ink/80 text-[16px] md:text-[17px] font-light leading-[1.85]">
              {copy("howwework_body_2", "We work through long-standing local relationships and handle the details quietly in the background, so your time away feels considered, personal, and well supported from start to finish.")}
            </p>
            <p className="text-ink/80 text-[16px] md:text-[17px] font-light leading-[1.85]">
              {copy("howwework_body_3", "We design each journey ourselves. We answer our own emails. We pick up our own phones. And we travel, frequently, to the places we send you.")}
            </p>
          </div>
        </div>
      </section>

      <section className="bg-sand py-24 md:py-32" data-testid="about-founders">
        <div className="max-w-[1500px] mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-5">
            <img src={mediaUrl(copy("founders_image", TEAM))} alt="A quiet morning at the office" className="w-full aspect-[4/5] object-cover" />
          </div>
          <div className="md:col-span-7 md:pl-8">
            <p className="eyebrow">{copy("founders_eyebrow", "The Founders")}</p>
            <h2 className="mt-5 font-serif text-4xl md:text-5xl text-jade font-light leading-tight">{copy("founders_h2", "Allister & John.")}</h2>
            <p className="mt-6 text-ink/80 text-[16px] font-light leading-relaxed max-w-xl">
              {copy("founders_body_1", "Allister and John founded Abbot Asia after twenty years between them living and travelling across the region. Their conviction was simple. Asia rewards thoughtful travel, and every traveller deserves a journey shaped with care, knowledge, and time.")}
            </p>
            <p className="mt-4 text-ink/80 text-[16px] font-light leading-relaxed max-w-xl">
              {copy("founders_body_2", "They still design every journey personally, alongside our small team of country specialists.")}
            </p>
          </div>
        </div>
      </section>

      <section className="bg-ivory py-24 md:py-32" data-testid="about-specialists">
        <div className="max-w-[1500px] mx-auto px-6 md:px-12">
          <p className="eyebrow mb-10">{copy("specialists_eyebrow", "The Specialists")}</p>
          <h2 className="font-serif text-4xl md:text-5xl text-jade font-light leading-tight max-w-2xl">
            {copy("specialists_h2", "The team behind every journey.")}
          </h2>
          <p className="mt-6 text-ink/70 text-[16px] font-light leading-relaxed max-w-xl">
            {copy("specialists_intro", "Each of our specialists has lived in the region they look after. You will work with one of them, by name, from your first email onwards.")}
          </p>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-10 md:gap-8">
            {specialists.map((s, i) => (
              <div key={s.id || s.name} data-testid={`specialist-${s.id || s.name}`} className={`fade-up delay-${(i % 4) + 1} border-t border-mist/60 pt-6`}>
                {s.image && (
                  <img src={mediaUrl(s.image)} alt={s.name} className="w-full aspect-[4/5] object-cover mb-5" />
                )}
                <p className="font-serif text-3xl text-jade italic">{s.name}</p>
                <p className="mt-3 eyebrow text-ink/55 text-[10px]">{s.role}</p>
                {s.bio && <p className="mt-3 text-ink/70 text-[13px] font-light leading-relaxed">{s.bio}</p>}
              </div>
            ))}
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
